import { useRef, type KeyboardEvent, type ReactNode } from "react";

import { useGlide } from "@/lib/useGlide";

import styles from "./Tabs.module.css";

export interface TabItem<K extends string> {
  key: K;
  label: ReactNode;
  panel: ReactNode;
}

interface TabsProps<K extends string> {
  /** The accessible name of the tab list. */
  label: string;
  items: readonly TabItem<K>[];
  selected: K;
  onSelect: (key: K) => void;
  /** Ids are `${idPrefix}-tab-${key}` and `${idPrefix}-panel-${key}`. */
  idPrefix: string;
  /** The tab buttons by key, for a caller that moves focus itself. */
  tabRef?: (key: K, element: HTMLButtonElement | null) => void;
  /** On the tab list, next to the block's own class. */
  className?: string;
  /** On every panel, next to the block's own class. */
  panelClassName?: string;
  /**
   * Tools at the end of the tab row — the record's intake (Task 9). With
   * them the row and the tools stand in one head that carries the hairline,
   * and `className` goes on that head. `null` keeps the head without tools.
   */
  end?: ReactNode;
}

/**
 * A row of tabs with a marker that glides under the selected one, and the
 * panels they control (BW-5). One tab stop; ←/→ move and select — selection
 * follows focus — and wrap, Home and End go to the ends.
 *
 * Every panel is in the page and the inactive ones are `hidden`, so each
 * tab's `aria-controls` names something that exists; only the selected panel
 * holds its content, because nobody sees the others. The row and its panels
 * come back as siblings, so the caller's own layout (a column with a gap, in
 * the record) decides how they sit.
 *
 * The marker is an `aria-hidden` bar placed from the selected tab's offset and
 * width (`useGlide`); it replaces the selected tab's own border.
 */
export function Tabs<K extends string>({
  label,
  items,
  selected,
  onSelect,
  idPrefix,
  tabRef,
  className,
  panelClassName,
  end,
}: TabsProps<K>) {
  const listRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<HTMLSpanElement>(null);
  useGlide("tab", listRef, markerRef, items.length, selected);
  // A `selected` that is none of the items still leaves the row reachable.
  const stop = Math.max(
    0,
    items.findIndex((item) => item.key === selected),
  );

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Alt+← is the browser's Back, not a tab: a modified key is not ours.
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const count = items.length;
    const index = items.findIndex((item) => item.key === selected);
    const target =
      event.key === "ArrowRight"
        ? (index + 1) % count
        : event.key === "ArrowLeft"
          ? (Math.max(index, 0) + count - 1) % count
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? count - 1
              : undefined;
    const item = target === undefined ? undefined : items[target];
    if (target === undefined || item === undefined) return;
    event.preventDefault();
    onSelect(item.key);
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[target]?.focus();
  }

  // A head as soon as there may be tools — `null` too, so the row keeps its
  // place in the tree (and the focus in it) when the tools come and go.
  const headed = end !== undefined;
  const listClass = headed
    ? `${styles.tabs} ${styles.tabsInHead}`
    : className
      ? `${styles.tabs} ${className}`
      : styles.tabs;
  const list = (
    <div ref={listRef} role="tablist" aria-label={label} className={listClass} onKeyDown={onKeyDown}>
      {items.map((item, index) => (
        <button
          key={item.key}
          ref={(element) => tabRef?.(item.key, element)}
          type="button"
          role="tab"
          id={`${idPrefix}-tab-${item.key}`}
          aria-selected={selected === item.key}
          aria-controls={`${idPrefix}-panel-${item.key}`}
          tabIndex={index === stop ? 0 : -1}
          className={styles.tab}
          onClick={() => onSelect(item.key)}
        >
          {item.label}
        </button>
      ))}
      <span ref={markerRef} className={styles.marker} aria-hidden="true" />
    </div>
  );

  return (
    <>
      {headed ? (
        <div className={className ? `${styles.head} ${className}` : styles.head}>
          {list}
          <div className={styles.end}>{end}</div>
        </div>
      ) : (
        list
      )}
      {items.map((item) => (
        <div
          key={item.key}
          role="tabpanel"
          id={`${idPrefix}-panel-${item.key}`}
          aria-labelledby={`${idPrefix}-tab-${item.key}`}
          hidden={selected !== item.key}
          className={panelClassName ? `${styles.panel} ${panelClassName}` : styles.panel}
        >
          {selected === item.key ? item.panel : null}
        </div>
      ))}
    </>
  );
}
